import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../utils/axiosInstance';
import { rfpApi, getOptionalAuthHeaders } from '../config/api';

export const uploadRfpPdf = createAsyncThunk(
  'rfp/uploadRfpPdf',
  async (file, { rejectWithValue, getState }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const headers = getOptionalAuthHeaders(getState);
      delete headers['Content-Type'];

      const response = await axios.post(rfpApi('/upload'), formData, {
        headers,
      });

      const fileId = response.data?.data?.id;
      if (!fileId) throw new Error('Missing file ID');

      return { fileId };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);


export const analyzeRfpById = createAsyncThunk(
  'rfp/analyzeRfpById',
  async (fileId, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(
        rfpApi(`/${fileId}/analyze`),
        { headers: getOptionalAuthHeaders(getState) },
      );
      return {
        fileId,
        analyzeSummary: response.data?.data.analysisToText,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
export const searchSuppliers = createAsyncThunk(
  'rfp/searchSuppliers',
  async ({ text, page = 1, pageSize = 20 }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.post(
        `${rfpApi('/search')}?page=${page}&limit=${pageSize}`,
        { text },
        { headers: getOptionalAuthHeaders(getState) },
      );
      return response.data?.data?.topTenResults || [];
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
export const searchMoreRecSuppliers = createAsyncThunk(
  'rfp/searchMoreRecSuppliers',
  async ({ text, page = 1, pageSize = 20 }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.post(
        `${rfpApi('/search')}?page=${page}&pageSize=${pageSize}`,
        { text },
        { headers: getOptionalAuthHeaders(getState) },
      );
      return response.data?.data?.topTenResults || [];
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const searchSuppliersByPromtText = createAsyncThunk(
  'rfp/searchSuppliersByPromtText',
  async ({ text, page = 1, pageSize = 20 }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.post(
        `${rfpApi('/search')}?page=${page}&pageSize=${pageSize}`,
        { text },
        { headers: getOptionalAuthHeaders(getState) },
      );
      return response.data?.data?.topTenResults || [];
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);




const rfpSlice = createSlice({
  name: 'rfp',
  initialState: {
  importCompleted: false,
  recSuppliers: [],
  moreRecSuppliers: [],
  searchSuppliers: [],
  loading: false,
  error: null,
  summaryText: '',
  fileId: null,
  analyzing: false, 
  },
  reducers: {
    resetRfpState: (state) => {
      state.importCompleted = false;
      state.loading = false;
      state.error = null;
      state.summaryText = '';
    },
  },
extraReducers: (builder) => {
  builder
    .addCase(uploadRfpPdf.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(uploadRfpPdf.fulfilled, (state, action) => {
      state.importCompleted = true;
      state.loading = false;
      state.fileId = action.payload.fileId;
    })
    .addCase(uploadRfpPdf.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || 'Upload failed';
    })
    .addCase(analyzeRfpById.pending, (state) => {
      state.analyzing = true;
    })
    .addCase(analyzeRfpById.fulfilled, (state, action) => {
      state.analyzing = false;
      state.summaryText = action.payload.analyzeSummary;
    })
    .addCase(analyzeRfpById.rejected, (state, action) => {
      state.analyzing = false;
      state.error = action.payload || 'Analyze failed';
    })
    .addCase(searchSuppliers.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(searchSuppliers.fulfilled, (state, action) => {
      state.loading = false;
      state.recSuppliers = action.payload;
    })
    .addCase(searchSuppliers.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || 'Search failed';
    })
    .addCase(searchMoreRecSuppliers.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(searchMoreRecSuppliers.fulfilled, (state, action) => {
      state.loading = false;
      state.moreRecSuppliers = action.payload;
    })
    .addCase(searchMoreRecSuppliers.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || 'Search failed';
    })
    .addCase(searchSuppliersByPromtText.fulfilled, (state, action) => {
      state.loading = false;
      state.searchSuppliers = action.payload;
    })

}
});

export const { resetRfpState } = rfpSlice.actions;

export default rfpSlice.reducer;
